import { compose, createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import * as Sentry from '@sentry/react';
import { StoreState } from 'types/redux';
import CandidatePortalWindow from 'types/window';
import rootReducer from '../reducers';
import { authContextMirror } from '../providers/authContextMirror';

// Vite statically replaces `process.env.NODE_ENV` with the literal
// 'production' / 'development' string in client bundles, the SSG
// pipeline sets it explicitly via `NODE_ENV=production node ./scripts/ssg`,
// and Jest sets it to 'test'. One read covers all three callers and
// avoids `import.meta` syntax that would fail to parse under Jest's
// CommonJS runtime.
const isProduction = process.env.NODE_ENV === 'production';

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Defaults to true in @sentry/react v8, which uploads the *raw* store state
  // as a `redux_state.json` attachment before `beforeSend` can scrub it.
  // The sanitized state is still sent via `event.contexts.state`.
  attachReduxState: false,
  actionTransformer: action => {
    if (
      action?.type === '@@redux-form/CHANGE' &&
      action?.meta?.field === 'ssn'
    ) {
      return {
        ...action,
        payload: null,
      };
    }
    return action;
  },
});

const configureStore = (preloadedState?: StoreState) => {
  const composeEnhancers = isProduction
    ? compose
    : (window as CandidatePortalWindow).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ||
      compose;

  const enhancers = isProduction
    ? compose(applyMiddleware(thunk, authContextMirror), sentryReduxEnhancer)
    : composeEnhancers(applyMiddleware(thunk, authContextMirror));

  const store = createStore(rootReducer, preloadedState, enhancers);

  if (!isProduction) {
    (window as CandidatePortalWindow).store = store;
  }

  return store;
};

export default configureStore;
